import { graphql, navigate, PageProps } from 'gatsby';
import React, {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useKey, useVideo } from 'react-use';
import SEO from '../components/seo';
import Spinner from '../components/spinner';
import TransitionLink from '../components/transition-link';
import VideoControls from '../components/video-controls';
import useHlsVideo from '../hooks/use-hls-video';
import Project from '../types/project';

type ProjectPageProps = PageProps<
  {
    sanityProject: Project;
  },
  Record<string, unknown>,
  {
    from?: string;
  }
>;

const ProjectPage: FunctionComponent<ProjectPageProps> = ({
  data: { sanityProject: project },
  location,
}) => {
  const [showSpinner, setShowSpinner] = useState(true);
  const fullScreenRef = useRef<HTMLDivElement>(null);

  const [video, videoState, videoControls, videoRef] = useVideo(
    <video autoPlay className="w-full h-full max-h-screen" controls={false} />,
  );

  useHlsVideo(
    project.video.videoM3u8Url,
    videoRef as RefObject<HTMLVideoElement>,
  );

  const toggleVideoPlaying = useCallback(() => {
    if (videoState.paused) {
      videoControls.play();
      return;
    }

    videoControls.pause();
  }, [videoControls, videoState.paused]);

  const hideSpinner = useCallback(() => {
    setShowSpinner(false);
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return () => {
        //
      };
    }

    videoElement.addEventListener('click', toggleVideoPlaying);

    return () => videoElement.removeEventListener('click', toggleVideoPlaying);
  }, [toggleVideoPlaying, videoRef]);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return () => {
        //
      };
    }

    videoElement.addEventListener('loadeddata', hideSpinner);

    return () => videoElement.removeEventListener('loadeddata', hideSpinner);
  }, [hideSpinner, videoRef]);

  useKey('Escape', () => navigate(location?.state?.from || '/artists/'));
  useKey(' ', toggleVideoPlaying, {}, [toggleVideoPlaying]);

  return (
    <div className="absolute top-0 bottom-0 left-0 right-0 z-10 flex flex-col justify-between h-full bg-white xl:bg-black">
      <SEO title={`ADEUS FILMS — ${project.name}`} />
      <div className="absolute top-0 z-10 flex justify-between w-full">
        <p className="pl-20 uppercase pointer-events-none text-black-lighter xl:pl-25 pt-30 xl:pt-15 text-14 sm:text-16 md:text-18 xl:text-16 2xl:text-1vw xl:leading-32 2xl:leading-normal tracking-0 xl:text-white">
          {project.name}
        </p>
        <TransitionLink
          className="px-20 text-black-lighter xl:px-25 text-14 sm:text-16 md:text-18 xl:text-16 2xl:text-1vw xl:leading-32 2xl:leading-normal tracking-0 py-30 xl:py-15 xl:text-white xl:hover:underline"
          to={location?.state?.from || '/artists/'}
        >
          CLOSE
        </TransitionLink>
      </div>
      <div className="relative flex-1" ref={fullScreenRef}>
        {video}
        <VideoControls
          currentTime={videoState.time}
          duration={videoState.duration}
          fullScreenRef={fullScreenRef}
          isPaused={videoState.paused}
          pause={videoControls.pause}
          play={videoControls.play}
          seek={videoControls.seek}
        />
      </div>
      {showSpinner && (
        <div className="absolute inset-0 flex items-center justify-center text-black xl:text-white">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ProjectPage;

export const query = graphql`
  query ProjectPageQuery($slug: String!) {
    sanityProject(slug: { current: { eq: $slug } }) {
      category
      date
      id
      name
      slug {
        current
      }
      video {
        videoM3u8Url
      }
    }
  }
`;
